import PropTypes from 'prop-types';
import * as React from 'react';
import {connect} from 'react-redux';
import ReactJoyride from 'react-joyride';

import * as selectors from '../onboarding-selectors';
import * as actions from '../onboarding-actions';

// eslint-disable-next-line import/no-commonjs
require('react-joyride/lib/styles/react-joyride.css'); // Override these styles in custom-properties.js

const MAX_RETRIES = 3;
let retriesMade = 0;

const mapStateToProps = function(state) {
    return {
        steps: selectors.getActiveSteps(state),
        activeStep: selectors.getActiveStep(state),
        activeJoyrideId: selectors.getActiveJoyrideId(state),
        run:
            selectors.getIsAnyJoyrideActive(state) &&
            !selectors.getIsJoyrideTemporarilyStopped(state),
        stepIndex: selectors.getActiveStepIndex(state),
        joyrideEnabled: selectors.getIsRehydrated(state) && selectors.getIsEnabled(state),
        finishButtonText: selectors.getFinishButtonText(state),
        isDemoTour: selectors.getIsActiveJoyrideDemoTour(state),
    };
};

const mapDispatchToProps = function(dispatch) {
    return {
        onJoyrideFinished: () => {
            dispatch(actions.finishActiveJoyride());
        },
        onJoyrideStepChangedCallback({step, index, action}, isLastStep) {
            if (action === 'next') {
                dispatch(actions.completeJoyrideStep(step, index));
            } else if (action === 'back' && isLastStep) {
                dispatch(actions.finishActiveJoyrideAndExit(index));
            } else if (action === 'back') {
                dispatch(actions.moveToPreviousStep(step, index));
            }
        },
        onJoyrideClose(step) {
            dispatch(actions.stopAndPersistActiveJoyride(step));
        },
        stopJoyride() {
            dispatch(actions.stopActiveJoyride());
        },
        startJoyride(id) {
            dispatch(actions.startJoyrideById(id));
        },
        handleAnimationsStarted() {
            dispatch(actions.disableJoyride());
        },
        handleAnimationsFinished() {
            dispatch(actions.enableJoyride());
        },
    };
};

class JoyrideComponent extends React.Component {
    static propTypes = {
        steps: PropTypes.array,
        stepIndex: PropTypes.number,
        activeStep: PropTypes.object,
        activeJoyrideId: PropTypes.string,
        finishButtonText: PropTypes.string.isRequired,
        type: PropTypes.string,
        run: PropTypes.bool,
        joyrideEnabled: PropTypes.bool,
        isDemoTour: PropTypes.bool,
        onJoyrideFinished: PropTypes.func,
        onJoyrideStepChangedCallback: PropTypes.func,
        onJoyrideStepStarted: PropTypes.func,
        onJoyrideClose: PropTypes.func,
        startJoyride: PropTypes.func.isRequired,
        stopJoyride: PropTypes.func.isRequired,
        handleAnimationsStarted: PropTypes.func.isRequired,
        handleAnimationsFinished: PropTypes.func.isRequired,
    };

    static defaultProps = {
        type: 'continuous',
        debug: false,
        autoStart: true,
        disableOverlay: true,
    };

    componentDidMount() {
        window.addEventListener('positionAnimationsStarted', this.props.handleAnimationsStarted);
        window.addEventListener('positionAnimationsFinished', this.props.handleAnimationsFinished);
    }

    render() {
        const {joyrideEnabled, finishButtonText, activeStep, ...otherProps} = this.props;

        if (!joyrideEnabled) {
            return null;
        }

        // Somehow, we have seen non-demosilo instances have paused joyrides, which causes warnings
        // and/or unexpected behavior.  Not sure how they get into that state, but if they do, we
        // should not try to show a joyride.  Demo tours should only happen in demo silos.
        if (
            this.props.isDemoTour &&
            typeof window.NutClientConfig !== 'undefined' &&
            !window.NutClientConfig.isDemoSilo
        ) {
            return null;
        }

        const locale = {
            last: finishButtonText,
            back: this.isLastStep(this.props.stepIndex, this.props.steps) ? 'End' : 'Back',
            close: 'Close',
            next: this.props.stepIndex === 0 && this.props.isDemoTour ? 'Start' : 'Next',
            skip: 'Skip',
        };

        return (
            <ReactJoyride
                {...otherProps}
                {...activeStep}
                locale={locale}
                callback={this.joyrideCallback}
            />
        );
    }

    joyrideCallback = (event) => {
        const joyrideWasClosed = event.action === 'close' || event.action === 'esc';

        if (event.type === 'finished' && this.props.onJoyrideFinished) {
            this.props.onJoyrideFinished();
        }
        if (event.type === 'step:after' || event.type === 'tooltip:after') {
            retriesMade = 0;
            if (joyrideWasClosed) {
                this.props.onJoyrideClose(event.step);
            } else {
                this.props.onJoyrideStepChangedCallback(
                    event,
                    this.isLastStep(event.index, this.props.steps)
                );
            }
        }
        // This is a super-ugly way of just simply restarting the joyride after
        // half a second if the target can't be found.  The hope is that after
        // that long, animations will be finished.
        // TODO (@ianvs): Don't rely on a 500ms guess.  Use some kind of event instead?
        if (event.type === 'error:target_not_found') {
            if (retriesMade < MAX_RETRIES) {
                retriesMade += 1;
                const joyrideId = this.props.activeJoyrideId;
                if (joyrideId) {
                    this.props.stopJoyride();
                    setTimeout(() => {
                        this.props.startJoyride(joyrideId);
                    }, 500);
                }
            } else {
                // TODO (@ianvs): handle this more gracefully than just giving up
                this.props.stopJoyride();
            }
        }
    };

    isLastStep = (stepIndex, steps) => {
        if (!steps) return false;
        if (!this.props.isDemoTour) return false;

        return steps.length - 1 === stepIndex;
    };
}

export const Joyride = connect(mapStateToProps, mapDispatchToProps)(JoyrideComponent);
